import { CSSProperties, useEffect } from 'react';
import { KIT_FONT_FACES, KIT_FONT_IDS, type KitFontId } from 'fym-shared/src/music24/releaseThemes';
import { kitFontWebFile } from 'fym-shared/src/music24/releaseKitLookSamples';

const FAMILY_PREFIX = 'M24 Kit ';

function publicUrl(file: string): string {
  return `${process.env.PUBLIC_URL ?? ''}/${file}`;
}

/** The WOFF2 web copy of a kit font (`pnpm look:render-samples` writes it). */
export function kitFontUrl(fontId: KitFontId): string {
  return publicUrl(kitFontWebFile(fontId));
}

/**
 * The name of a kit font as the UI shows it: the family, with no `M24 Kit` prefix ("Inter Tight",
 * "Anton"). A font name is a proper noun and needs no translation.
 */
export function kitFontName(fontId: KitFontId): string {
  const { family } = KIT_FONT_FACES[fontId];
  return family.startsWith(FAMILY_PREFIX) ? family.slice(FAMILY_PREFIX.length) : family;
}

/** The inline style that shows a text in a kit font, in its display weight and its slant. */
export function kitFontStyle(fontId: KitFontId): CSSProperties {
  const face = KIT_FONT_FACES[fontId];
  return {
    fontFamily: `'${face.family}', ${face.fallback}`,
    fontWeight: face.displayWeight,
    fontStyle: face.italic ? 'italic' : 'normal',
  };
}

/** The fonts that loaded: they stay in `document.fonts` for the page. */
const loaded = new Set<KitFontId>();
/** The load in progress, if any: calls at the same time share it. */
let loading: Promise<void> | null = null;

/** Adds one kit font to the page and waits for it. Rejects when the file does not load. */
async function loadKitWebFont(fontId: KitFontId): Promise<void> {
  const face = KIT_FONT_FACES[fontId];
  const [low, high] = face.weights;
  const fontFace = new FontFace(face.family, `url(${kitFontUrl(fontId)}) format('woff2')`, {
    display: 'swap',
    weight: low === high ? String(low) : `${low} ${high}`,
    style: face.italic ? 'italic' : 'normal',
  });
  await fontFace.load();
  document.fonts.add(fontFace);
  loaded.add(fontId);
}

/**
 * Adds the 9 kit fonts (the renderer's own faces, WOFF2) to the page, so the font chips, the theme
 * still, the style previews and the cover editor export show each font in its face. Each font loads
 * by itself (review fix): one font that fails does not stop the others, and a later call tries the
 * fonts that failed again. A font that did not load shows its generic fallback. A browser with no
 * `FontFace` (a test) keeps the fallbacks. Never rejects.
 */
export function loadKitWebFonts(): Promise<void> {
  if (loading !== null) {
    return loading;
  }
  if (typeof FontFace === 'undefined' || typeof document === 'undefined' || document.fonts === undefined) {
    return Promise.resolve();
  }
  const missing = KIT_FONT_IDS.filter((fontId) => !loaded.has(fontId));
  if (missing.length === 0) {
    return Promise.resolve();
  }
  loading = Promise.allSettled(missing.map(loadKitWebFont)).then((results) => {
    loading = null;
    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      // A font that does not load keeps its fallback face; the next call tries it again.
      console.warn(`${failed.length} kit font(s) did not load`, failed);
    }
  });
  return loading;
}

/** Tests only: forgets the loaded fonts. */
export function resetKitWebFontsForTests(): void {
  loaded.clear();
  loading = null;
}

/** Loads the kit fonts when a theme UI mounts. */
export function useKitWebFonts(): void {
  useEffect(() => {
    loadKitWebFonts().catch(console.error);
  }, []);
}
